import React, { useCallback, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import axios from "src/utils/axios";
import { getDeviceId } from "src/utils/deviceId";

/** Texto del aviso (función pura, para los tests). */
export function takeoverText(deviceName) {
  return `Este empleado ya está siendo utilizado en otro dispositivo${deviceName ? ` («${deviceName}»)` : ""}. Si continúas, ese dispositivo volverá a la pantalla de selección de empleado.`;
}

/**
 * 2026-10-06 · «Un empleado solo en un dispositivo»: antes de identificarse
 * con un empleado que ya está activo en otro dispositivo, se avisa con
 * Cancelar / Continuar (diálogo de la app, nunca window.confirm). Solo avisa
 * si el restaurante tiene la opción encendida y el empleado no puede estar
 * en varios (lo decide el API: `employeePinAuth/inUse/{id}`). Si la consulta
 * falla, no se bloquea: la toma real la hace el login (gana quien entra).
 *
 * Uso: const { confirmTakeover, takeoverDialog } = useEmployeeTakeoverConfirm();
 *      if (!(await confirmTakeover(emp))) return;   // y renderizar {takeoverDialog}
 */
export default function useEmployeeTakeoverConfirm() {
  const [state, setState] = useState(null); // { name, deviceName }
  const resolver = useRef(null);

  const close = useCallback((ok) => {
    setState(null);
    const r = resolver.current;
    resolver.current = null;
    if (r) r(ok);
  }, []);

  const confirmTakeover = useCallback(async (emp) => {
    if (!emp?.id || emp.isAdmin) return true;
    let info = null;
    try {
      const res = await axios.get(`employeePinAuth/inUse/${emp.id}`, { params: { deviceId: getDeviceId() } });
      info = res?.data || null;
    } catch {
      return true;
    }
    if (!info?.inUse) return true;
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({ name: emp.name, deviceName: info.deviceName || null });
    });
  }, []);

  const takeoverDialog = (
    <Dialog open={!!state} onClose={() => close(false)} data-testid="employee-takeover-dialog" sx={{ zIndex: 2100 }}>
      <DialogTitle>{state?.name ? `${state.name} está en uso` : "Empleado en uso"}</DialogTitle>
      <DialogContent>
        <DialogContentText>{takeoverText(state?.deviceName)}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close(false)} data-testid="employee-takeover-cancel">Cancelar</Button>
        <Button variant="contained" onClick={() => close(true)} data-testid="employee-takeover-continue">Continuar</Button>
      </DialogActions>
    </Dialog>
  );

  return { confirmTakeover, takeoverDialog };
}
